import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod/v3'
import { createFileRoute } from '@tanstack/react-router'

import type { UpdateEInvoiceSettingsRequest } from '@/lib/api/einvoice-api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    useInvoiceSettings,
    useUpdateInvoiceSettings,
} from '@/hooks/use-invoice-query'
import {
    useEInvoiceSettings,
    useUpdateEInvoiceSettings,
} from '@/hooks/use-einvoice-query'
import { ApiError } from '@/lib/api/client'
import { uploadFile } from '@/lib/api/files-api'
import { getProductImageURL } from '@/lib/product-image'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute('/_app/_authed/_sidebar/settings/invoice')(
    {
        component: InvoiceSettingsPage,
    },
)

function InvoiceSettingsPage() {
    const { data: settings, isLoading } = useInvoiceSettings()
    const updateMutation = useUpdateInvoiceSettings()
    const [isUploading, setIsUploading] = useState(false)
    const [uploadError, setUploadError] = useState('')

    const schema = z.object({
        company_name: z.string().min(1, m.validation_required()),
        company_address: z.string().min(1, m.validation_required()),
        company_phone: z.string().min(1, m.validation_required()),
        company_tax_code: z.string().optional(),
        company_logo: z.string().nullable(),
        bank_name: z.string().optional(),
        bank_account: z.string().optional(),
        bank_holder: z.string().optional(),
        footer_note: z.string().optional(),
        vat_percent: z.coerce.number().min(0).max(100),
    })

    type FormData = z.infer<typeof schema>

    const form = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: {
            company_name: '',
            company_address: '',
            company_phone: '',
            company_tax_code: '',
            company_logo: null,
            bank_name: '',
            bank_account: '',
            bank_holder: '',
            footer_note: '',
            vat_percent: 0,
        },
    })

    // Populate form when server data loads
    useEffect(() => {
        if (settings) {
            form.reset({
                company_name: settings.company_name,
                company_address: settings.company_address,
                company_phone: settings.company_phone,
                company_tax_code: settings.company_tax_code ?? '',
                company_logo: settings.company_logo ?? null,
                bank_name: settings.bank_name ?? '',
                bank_account: settings.bank_account ?? '',
                bank_holder: settings.bank_holder ?? '',
                footer_note: settings.footer_note ?? '',
                vat_percent: settings.vat_percent,
            })
        }
    }, [settings])

    const logo = form.watch('company_logo')
    const busy = isUploading || updateMutation.isPending
    const handleUpload = async (file: File) => {
        setUploadError('')
        if (
            !['image/png', 'image/jpeg', 'image/webp'].includes(file.type) ||
            file.size > 2 * 1024 * 1024
        ) {
            setUploadError(m.invoice_logo_requirements())
            return
        }
        setIsUploading(true)
        try {
            const uploaded = await uploadFile(file, true)
            form.setValue('company_logo', uploaded.url, { shouldDirty: true })
        } catch {
            setUploadError(m.toast_packing_photo_upload_error())
        } finally {
            setIsUploading(false)
        }
    }

    const handleSubmit = form.handleSubmit(async (data) => {
        if (busy) return
        await updateMutation.mutateAsync({
            ...data,
            company_tax_code: data.company_tax_code || undefined,
            bank_name: data.bank_name || undefined,
            bank_account: data.bank_account || undefined,
            bank_holder: data.bank_holder || undefined,
            footer_note: data.footer_note || undefined,
        })
    })

    if (isLoading) {
        return (
            <div className="text-sm text-muted-foreground">
                {m.common_loading()}
            </div>
        )
    }

    return (
        <div className="max-w-2xl space-y-6">
            <h1 className="text-xl font-semibold">
                {m.invoice_settings_title()}
            </h1>
            <FormProvider {...form}>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>{m.invoice_company_info()}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <Field
                                name="company_logo"
                                label={m.invoice_company_logo()}
                            >
                                {logo ? (
                                    <img
                                        src={getProductImageURL(
                                            logo,
                                            'original',
                                        )}
                                        alt={m.invoice_company_logo()}
                                        className="mb-2 h-24 max-w-full object-contain"
                                    />
                                ) : null}
                                <Input
                                    id="company_logo"
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    disabled={busy}
                                    aria-describedby="logo-help"
                                    onChange={(event) => {
                                        const file = event.target.files?.[0]
                                        event.target.value = ''
                                        if (file) void handleUpload(file)
                                    }}
                                />
                                <p
                                    id="logo-help"
                                    className="text-sm text-muted-foreground"
                                >
                                    {m.invoice_logo_requirements()}
                                </p>
                                {isUploading ? (
                                    <p role="status">{m.common_loading()}</p>
                                ) : null}
                                {uploadError ? (
                                    <p
                                        role="alert"
                                        className="text-sm text-destructive"
                                    >
                                        {uploadError}
                                    </p>
                                ) : null}
                                {logo ? (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        disabled={busy}
                                        onClick={() => {
                                            form.setValue(
                                                'company_logo',
                                                null,
                                                { shouldDirty: true },
                                            )
                                            setUploadError('')
                                        }}
                                    >
                                        {m.common_remove()}
                                    </Button>
                                ) : null}
                            </Field>
                            <Field
                                name="company_name"
                                label={m.invoice_company_name()}
                                required
                            >
                                <Input
                                    id="company_name"
                                    {...form.register('company_name')}
                                />
                            </Field>

                            <Field
                                name="company_address"
                                label={m.invoice_company_address()}
                                required
                            >
                                <Input
                                    id="company_address"
                                    {...form.register('company_address')}
                                />
                            </Field>

                            <div className="grid grid-cols-2 gap-4">
                                <Field
                                    name="company_phone"
                                    label={m.invoice_company_phone()}
                                    required
                                >
                                    <Input
                                        id="company_phone"
                                        {...form.register('company_phone')}
                                    />
                                </Field>
                                <Field
                                    name="company_tax_code"
                                    label={m.invoice_company_tax()}
                                >
                                    <Input
                                        id="company_tax_code"
                                        {...form.register('company_tax_code')}
                                    />
                                </Field>
                            </div>

                            <Field name="vat_percent" label="VAT (%)">
                                <Input
                                    id="vat_percent"
                                    type="number"
                                    min={0}
                                    max={100}
                                    step={0.1}
                                    className="w-32"
                                    {...form.register('vat_percent')}
                                />
                            </Field>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>{m.invoice_bank_info()}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <Field
                                name="bank_name"
                                label={m.invoice_bank_name()}
                            >
                                <Input
                                    id="bank_name"
                                    {...form.register('bank_name')}
                                />
                            </Field>

                            <div className="grid grid-cols-2 gap-4">
                                <Field
                                    name="bank_account"
                                    label={m.invoice_bank_account()}
                                >
                                    <Input
                                        id="bank_account"
                                        {...form.register('bank_account')}
                                    />
                                </Field>
                                <Field
                                    name="bank_holder"
                                    label={m.invoice_bank_holder()}
                                >
                                    <Input
                                        id="bank_holder"
                                        {...form.register('bank_holder')}
                                    />
                                </Field>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>{m.invoice_footer()}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Field
                                name="footer_note"
                                label={m.invoice_footer_note()}
                            >
                                <Input
                                    id="footer_note"
                                    placeholder={m.invoice_footer_placeholder()}
                                    {...form.register('footer_note')}
                                />
                            </Field>
                        </CardContent>
                    </Card>

                    <div className="flex justify-end">
                        <Button type="submit" disabled={busy}>
                            {updateMutation.isPending
                                ? m.common_saving()
                                : m.invoice_save()}
                        </Button>
                    </div>
                </form>
            </FormProvider>
            <EInvoiceSettingsCard />
        </div>
    )
}

function EInvoiceSettingsCard() {
    const { data: settings, isPending, isError } = useEInvoiceSettings()
    const mutation = useUpdateEInvoiceSettings()
    const form = useForm<UpdateEInvoiceSettingsRequest>({
        defaultValues: {
            provider: '',
            api_url: '',
            username: '',
            password: '',
            misa_app_id: '',
            misa_tax_code: '',
            misa_inv_series: '',
            misa_sign_type: 5,
        },
    })
    useEffect(() => {
        if (settings) {
            const {
                has_password: _hasPassword,
                updated_at: _updatedAt,
                ...values
            } = settings
            form.reset({ ...values, password: '' })
        }
    }, [settings, form])
    const provider = form.watch('provider')
    const fields = [
        ['api_url', m.einvoice_settings_api_url()],
        ['username', m.einvoice_settings_username()],
        ['password', m.einvoice_settings_password()],
        ['misa_app_id', m.einvoice_settings_misa_app_id()],
        ['misa_tax_code', m.einvoice_settings_misa_tax_code()],
        ['misa_inv_series', m.einvoice_settings_misa_inv_series()],
    ] as const
    const submit = form.handleSubmit(async (values) => {
        if (mutation.isPending) return
        try {
            await mutation.mutateAsync(values)
            form.setValue('password', '')
        } catch (error) {
            if (error instanceof ApiError && error.status === 400) {
                let mapped = false
                if (error.details && typeof error.details === 'object') {
                    for (const [key, message] of Object.entries(
                        error.details,
                    )) {
                        if (key in values && typeof message === 'string') {
                            form.setError(
                                key as keyof UpdateEInvoiceSettingsRequest,
                                { message },
                            )
                            mapped = true
                        }
                    }
                }
                if (!mapped)
                    form.setError('root', {
                        message: m.einvoice_settings_validation_failed(),
                    })
            }
        }
    })
    return (
        <Card>
            <CardHeader>
                <CardTitle>{m.einvoice_settings_title()}</CardTitle>
            </CardHeader>
            <CardContent>
                {isPending ? (
                    <p role="status">{m.common_loading()}</p>
                ) : isError || !settings ? (
                    <p role="alert">{m.einvoice_settings_load_failed()}</p>
                ) : (
                    <FormProvider {...form}>
                        <form
                            onSubmit={submit}
                            className="space-y-4"
                            noValidate
                        >
                            <fieldset
                                disabled={mutation.isPending}
                                className="space-y-4"
                            >
                                <Field
                                    name="provider"
                                    label={m.einvoice_settings_provider()}
                                >
                                    <select
                                        id="provider"
                                        className="h-11 w-full rounded-md border border-input bg-background px-3 md:h-9"
                                        {...form.register('provider')}
                                    >
                                        <option value="">
                                            {m.einvoice_settings_disabled()}
                                        </option>
                                        <option value="misa">
                                            {m.einvoice_settings_misa()}
                                        </option>
                                    </select>
                                </Field>
                                {provider === 'misa' && (
                                    <>
                                        {fields.map(([name, label]) => (
                                            <Field
                                                key={name}
                                                name={name}
                                                label={label}
                                            >
                                                <Input
                                                    id={name}
                                                    type={
                                                        name === 'password'
                                                            ? 'password'
                                                            : 'text'
                                                    }
                                                    autoComplete={
                                                        name === 'password'
                                                            ? 'new-password'
                                                            : undefined
                                                    }
                                                    placeholder={
                                                        name === 'password' &&
                                                        settings.has_password
                                                            ? m.einvoice_settings_password_saved()
                                                            : undefined
                                                    }
                                                    {...form.register(name, {
                                                        validate: (value) =>
                                                            form.getValues(
                                                                'provider',
                                                            ) !== 'misa' ||
                                                            (name ===
                                                                'password' &&
                                                                settings.has_password) ||
                                                            !!value.trim() ||
                                                            m.validation_required(),
                                                    })}
                                                />
                                            </Field>
                                        ))}
                                        <Field
                                            name="misa_sign_type"
                                            label={m.einvoice_settings_misa_sign_type()}
                                        >
                                            <select
                                                id="misa_sign_type"
                                                className="h-11 w-full rounded-md border border-input bg-background px-3 md:h-9"
                                                {...form.register(
                                                    'misa_sign_type',
                                                    { valueAsNumber: true },
                                                )}
                                            >
                                                <option value={5}>
                                                    {m.einvoice_settings_cash_register()}
                                                </option>
                                            </select>
                                        </Field>
                                    </>
                                )}
                                {form.formState.errors.root && (
                                    <p role="alert">
                                        {form.formState.errors.root.message}
                                    </p>
                                )}
                                <Button type="submit">
                                    {mutation.isPending
                                        ? m.common_saving()
                                        : m.einvoice_settings_save()}
                                </Button>
                            </fieldset>
                        </form>
                    </FormProvider>
                )}
            </CardContent>
        </Card>
    )
}
